import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Users, Wallet, Coins, TrendingUp, Receipt, Banknote, ShieldCheck } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="ob-card rounded-md p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{label}</span>
        <Icon size={18} className="text-[#00E5FF]" strokeWidth={1.5} />
      </div>
      <div className="font-num text-3xl font-extrabold text-white" dir="ltr">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
      <div className="glow-line mt-4 w-16" />
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><ShieldCheck className="text-[#00E5FF]" /> {t("adm.dashTitle")}</h1>
        <p className="text-zinc-500 text-sm mt-1">{t("adm.dashSub")}</p>
      </div>

      <div className="ob-card rounded-md p-8 relative overflow-hidden">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-zinc-400 uppercase">
          <Banknote size={14} /> {t("adm.liquidity")}
        </div>
        <div className="mt-3 font-num text-5xl font-extrabold text-white" dir="ltr" data-testid="total-liquidity">
          {fmtMoney(stats?.total_liquidity_base, 2)} <span className="text-2xl text-[#00E5FF]">{stats?.base_currency}</span>
        </div>
        <div className="glow-line mt-5 w-40" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={Users} label={t("adm.users")} value={stats?.users} sub={`${stats?.admins} ${t("adm.admins")}`} testid="stat-users" />
        <StatCard icon={Wallet} label={t("adm.activeAccounts")} value={stats?.open_accounts} testid="stat-accounts" />
        <StatCard icon={Coins} label={t("adm.currencies")} value={stats?.currencies} sub={`${stats?.active_currencies} ${t("adm.activeC")}`} testid="stat-currencies" />
        <StatCard icon={TrendingUp} label={t("adm.ratePairs")} value={stats?.rate_pairs} testid="stat-rates" />
        <StatCard icon={Receipt} label={t("adm.totalTxns")} value={stats?.transactions} testid="stat-transactions" />
      </div>
    </div>
  );
}
